import React, { useEffect, useState } from 'react';
import { Button, Group, List, NumberInput, Paper, ScrollArea, Stack, Table, Text, Title } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { TableContextProvider, Th, useTable } from '../../../global/hooks/useTable.tsx';
import tableTools from '../../../global/helpers/tableTools.ts';
import { Loading } from '../../../global/component/organism/Loading/Loading.tsx';
import { useGetHcpSwitchoverReadiness } from '../../../global/entities/hcpSwitchover/queries/useGetHcpSwitchoverReadiness.tsx';
import { HcpSwitchoverBlockerReason, IHcpSwitchoverBlocker, IHcpSwitchoverCandidate, IHcpSwitchoverLegacyHcp } from '../../../global/entities/hcpSwitchover/HcpSwitchover.ts';
import { useHcpSwitchoverDryRun } from '../../../global/entities/hcpSwitchover/mutations/useHcpSwitchoverDryRun.tsx';
import { useRunHcpSwitchoverBatch } from '../../../global/entities/hcpSwitchover/mutations/useRunHcpSwitchoverBatch.tsx';
import { useGetHcpSwitchoverLegacyHcps } from '../../../global/entities/hcpSwitchover/queries/useGetHcpSwitchoverLegacyHcps.tsx';
import { useSetHcpSwitchoverExclusion } from '../../../global/entities/hcpSwitchover/mutations/useSetHcpSwitchoverExclusion.tsx';
import { modals } from '@mantine/modals';

const BLOCKER_LABELS: Record<HcpSwitchoverBlockerReason, string> = {
    [HcpSwitchoverBlockerReason.NoMobile]: 'switchover.reason.noMobile',
    [HcpSwitchoverBlockerReason.InvalidMobile]: 'switchover.reason.invalidMobile',
    [HcpSwitchoverBlockerReason.SharedMobile]: 'switchover.reason.sharedMobile',
    [HcpSwitchoverBlockerReason.SmsBlocked]: 'switchover.reason.smsBlocked',
    [HcpSwitchoverBlockerReason.DefaultPassword]: 'switchover.reason.defaultPassword',
};

const SwitchoverList: React.FC = () => {
    const { t } = useTranslation();
    const { data: legacyHcps, isLoading } = useGetHcpSwitchoverLegacyHcps();
    const { data: readiness } = useGetHcpSwitchoverReadiness();
    const setExclusion = useSetHcpSwitchoverExclusion();
    const { search, setSorting, sortBy, reverseSortDirection } = useTable();

    if (isLoading) return <Loading loadingMessage={t('global.loading')} />;

    const blockerText = (blocker: IHcpSwitchoverBlocker) => t(BLOCKER_LABELS[blocker.reason], { name: blocker.sharedWith });
    // Sorted from the latest data on every render, so an exclusion shows at once even after a sort.
    const rows: IHcpSwitchoverLegacyHcp[] = sortBy
        ? tableTools.sortData(legacyHcps ?? [], { sortBy, reversed: reverseSortDirection, search })
        : (legacyHcps ?? []);

    return (
        <Stack gap="xs">
            {readiness?.length === 0 && <Text>{t('switchover.allReady')}</Text>}
            <ScrollArea>
                <Table verticalSpacing="xs">
                    <Table.Thead>
                        <Table.Tr>
                            <Th sorted={sortBy === 'name'} reversed={reverseSortDirection} onSort={() => setSorting('name', legacyHcps ?? [])}>
                                {t('switchover.nameColumn')}
                            </Th>
                            <Th sorted={sortBy === 'email'} reversed={reverseSortDirection} onSort={() => setSorting('email', legacyHcps ?? [])}>
                                {t('global.email')}
                            </Th>
                            <Table.Th>{t('switchover.reasonColumn')}</Table.Th>
                            <Table.Th />
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {rows.map((hcp) => (
                            <Table.Tr key={hcp.healthcareProfessionalID}>
                                <Table.Td>{hcp.name}</Table.Td>
                                <Table.Td>{hcp.email}</Table.Td>
                                <Table.Td>{hcp.blockers.map(blockerText).join(' · ')}</Table.Td>
                                <Table.Td>
                                    <Button
                                        size="xs"
                                        variant={hcp.excluded ? 'filled' : 'default'}
                                        color={hcp.excluded ? 'yellow' : undefined}
                                        aria-pressed={hcp.excluded}
                                        onClick={() => setExclusion.mutate({ healthcareProfessionalId: hcp.healthcareProfessionalID, excluded: !hcp.excluded })}
                                    >
                                        {t(hcp.excluded ? 'switchover.excluded' : 'switchover.exclude')}
                                    </Button>
                                </Table.Td>
                            </Table.Tr>
                        ))}
                    </Table.Tbody>
                </Table>
            </ScrollArea>
        </Stack>
    );
};

const DEFAULT_BATCH_SIZE = 20;
const MAX_BATCH_SIZE = 25;

const CandidateList: React.FC<{ title: string; candidates: IHcpSwitchoverCandidate[] }> = ({ title, candidates }) => (
    <Stack gap={4}>
        <Text fw="bold">{title}</Text>
        <List size="sm">
            {candidates.map((hcp) => (
                <List.Item key={hcp.healthcareProfessionalID}>{`${hcp.name} (${hcp.email})`}</List.Item>
            ))}
        </List>
    </Stack>
);

const BatchControls: React.FC = () => {
    const { t } = useTranslation();
    const { data: readiness } = useGetHcpSwitchoverReadiness();
    const { dataUpdatedAt: listUpdatedAt } = useGetHcpSwitchoverLegacyHcps();
    const dryRun = useHcpSwitchoverDryRun();
    const { reset: clearDryRun } = dryRun;
    const runBatch = useRunHcpSwitchoverBatch();

    // A preview taken before an exclusion or a batch may list HCPs that won't be switched any more.
    useEffect(() => clearDryRun(), [listUpdatedAt, clearDryRun]);
    const [batchSize, setBatchSize] = useState<number>(DEFAULT_BATCH_SIZE);
    const ready = readiness !== undefined && readiness.length === 0;
    const sizeEntered = batchSize >= 1;

    const confirmRunBatch = () =>
        modals.openConfirmModal({
            title: t('switchover.runBatch'),
            children: <Text>{t('switchover.runBatchQuestion', { count: batchSize })}</Text>,
            labels: { confirm: t('switchover.runBatchConfirm'), cancel: t('button.cancel') },
            onConfirm: () => runBatch.mutate({ batchSize }),
        });

    return (
        <Stack>
            <Group align="flex-end">
                <NumberInput
                    label={t('switchover.batchSize')}
                    min={1}
                    max={MAX_BATCH_SIZE}
                    allowDecimal={false}
                    value={batchSize}
                    onChange={(value) => setBatchSize(Number(value))}
                />
                <Button variant="default" disabled={!sizeEntered} loading={dryRun.isPending} onClick={() => dryRun.mutate({ batchSize })}>
                    {t('switchover.dryRun')}
                </Button>
                <Button disabled={!ready || !sizeEntered} loading={runBatch.isPending} onClick={confirmRunBatch}>
                    {t('switchover.runBatch')}
                </Button>
            </Group>
            {dryRun.data && <CandidateList title={t('switchover.dryRunResult')} candidates={dryRun.data} />}
            {dryRun.data && !ready && <Text size="sm" c="orange">{t('switchover.blockedNote')}</Text>}
            {runBatch.data && <CandidateList title={t('switchover.switchedResult')} candidates={runBatch.data} />}
        </Stack>
    );
};

const HcpSwitchoverPage: React.FC = () => {
    const { t } = useTranslation();

    return (
        <Paper radius="lg" shadow="sm" p="md">
            <Stack>
                <Title order={3}>{t('switchover.title')}</Title>
                <TableContextProvider>
                    <SwitchoverList />
                </TableContextProvider>
                <BatchControls />
            </Stack>
        </Paper>
    );
};

export default HcpSwitchoverPage;
